import { useCallback, useEffect, useRef, useState } from 'react'
import JuditProcessos from '@services/juditProcessos'
import { acceptJuditConsulta, acceptJuditProcessList, chooseJuditLocalConsulta, juditError, juditGenerationContext, juditRefreshWait, juditSelectable,
  juditReconcilesUncertain, juditSelection, juditSelectionError, juditUncertain, normalizeCnj, resolveJuditProcess, retainJuditSelections, validCnj, JUDIT_MAX_DOCUMENTS } from './juditDocumentHelpers'

export default function useJuditDocuments(publication, processReference = '') {
  const [configuration, setConfiguration] = useState(null)
  const [configurationError, setConfigurationError] = useState('')
  const [consulta, setConsulta] = useState(null)
  const [savedConsultas, setSavedConsultas] = useState([])
  const [maisConsultas, setMaisConsultas] = useState(false)
  const [instanceRequired, setInstanceRequired] = useState(false)
  const [selected, setSelected] = useState([])
  const [phase, setPhase] = useState('')
  const [error, setError] = useState('')
  const [uncertain, setUncertain] = useState(null)
  const [requestId, setRequestId] = useState('')
  const [requestHistory, setRequestHistory] = useState([])
  const [visibleText, setVisibleText] = useState(null)
  const [numeroProcesso, setNumeroProcesso] = useState('')
  const [instancia, setInstancia] = useState('')
  const [restoreId, setRestoreId] = useState('')
  const [requestLookupId, setRequestLookupId] = useState('')
  const operation = useRef(null)
  const currentConsulta = useRef(consulta)
  const currentUncertain = useRef(uncertain)
  currentConsulta.current = consulta
  currentUncertain.current = uncertain
  const resolvedProcess = resolveJuditProcess(publication, processReference)

  useEffect(() => {
    const controller = new AbortController()
    JuditProcessos.configuration(controller.signal).then((response) => {
      if (controller.signal.aborted) return
      if (response.success && response.data) setConfiguration(response.data)
      else setConfigurationError('Não foi possível consultar a disponibilidade da Judit. Os documentos já salvos continuam disponíveis para leitura local.')
    }).catch(() => { if (!controller.signal.aborted) setConfigurationError('A disponibilidade da Judit não foi confirmada.') })
    return () => { controller.abort(); operation.current?.abort() }
  }, [])

  const apply = useCallback((data, expected) => {
    const confirmed = acceptJuditConsulta(data, expected)
    setConsulta(confirmed)
    currentConsulta.current = confirmed
    setSelected((previous) => retainJuditSelections(confirmed, previous))
    setVisibleText((previous) => previous && confirmed.anexos.some((anexo) =>
      anexo.documentoId === previous.documentoId && anexo.documentoVersao === previous.versao) ? previous : null)
    setSavedConsultas((previous) => previous.some((item) => item.consultaId === confirmed.consultaId)
      ? previous.map((item) => item.consultaId === confirmed.consultaId ? confirmed : item)
      : [confirmed, ...previous.filter((item) => normalizeCnj(item.numeroProcesso) === normalizeCnj(confirmed.numeroProcesso))])
    return confirmed
  }, [])

  useEffect(() => {
    setSavedConsultas([])
    setMaisConsultas(false)
    setInstanceRequired(false)
    setInstancia('')
  }, [resolvedProcess.numeroProcesso])

  useEffect(() => {
    if (consulta && normalizeCnj(consulta.numeroProcesso) !== resolvedProcess.numeroProcesso) {
      setConsulta(null)
      currentConsulta.current = null
      setSelected([])
      setVisibleText(null)
    }
  }, [consulta, resolvedProcess.numeroProcesso])

  const perform = async (label, action, expected, mutation = false) => {
    if (operation.current || mutation && uncertain) return null
    const controller = new AbortController()
    operation.current = controller
    setPhase(label)
    setError('')
    const pending = () => ({ consultaId: expected.consultaId || null, requestId: expected.requestId || requestId,
      numeroProcesso: expected.numeroProcesso, instancia: expected.instancia,
      ...(expected.operacao === 'REOBTER' ? { operacao: 'REOBTER', anexoId: expected.anexoId, versaoAnterior: expected.versao } : {}) })
    try {
      const response = await action(controller.signal)
      if (controller.signal.aborted) return null
      if (!response.success) {
        setError(juditError(response))
        if (response.code === 'JUDIT_INSTANCE_REQUIRED') setInstanceRequired(true)
        if (mutation && juditUncertain(response)) setUncertain(pending())
        else if (mutation && expected.consultaId) setUncertain(pending())
        else if (expected.requestId) setRequestId('')
        return null
      }
      if (expected.operacao === 'REOBTER' && !juditReconcilesUncertain(pending(), response.data)) throw new Error('Unconfirmed re-download')
      const data = apply(response.data, expected)
      setNumeroProcesso(data.numeroProcesso)
      setInstancia(data.instancia)
      setRestoreId(data.consultaId)
      setInstanceRequired(false)
      setUncertain(null)
      return data
    } catch {
      if (!controller.signal.aborted) {
        setError('A resposta não pôde ser confirmada. Confira os documentos salvos deste processo; a operação não será repetida automaticamente.')
        if (mutation) setUncertain(pending())
      }
      return null
    } finally {
      if (operation.current === controller) { operation.current = null; setPhase('') }
    }
  }

  const readByProcess = useCallback(async (number) => {
    if (operation.current || !validCnj(number)) return null
    const controller = new AbortController()
    operation.current = controller
    setPhase('Lendo documentos salvos deste processo…')
    setError('')
    try {
      const response = await JuditProcessos.listByProcess(number, controller.signal)
      if (controller.signal.aborted) return null
      if (!response.success) { setError(juditError(response)); return null }
      const rows = acceptJuditProcessList(response.data, number)
      const more = response.data.maisConsultas === true
      setSavedConsultas(rows)
      setMaisConsultas(more)
      const pending = currentUncertain.current
      const previous = currentConsulta.current
      const chosen = chooseJuditLocalConsulta(rows, pending, previous, more)
      if (chosen) {
        apply(chosen, { numeroProcesso: number })
        setNumeroProcesso(chosen.numeroProcesso)
        setInstancia(chosen.instancia)
        setRestoreId(chosen.consultaId)
        setInstanceRequired(false)
        if (juditReconcilesUncertain(pending, chosen, { consultas: rows, maisConsultas: more })) setUncertain(null)
      } else {
        setConsulta(null)
        currentConsulta.current = null
        setSelected([])
        setVisibleText(null)
        if (pending) setError('A operação anterior ainda não pôde ser vinculada com segurança a uma consulta local. A incerteza foi mantida; nenhuma busca será repetida automaticamente.')
      }
      return rows
    } catch {
      if (!controller.signal.aborted) setError('Não foi possível confirmar os documentos salvos deste processo. Tente reler a lista local.')
      return null
    } finally {
      if (operation.current === controller) { operation.current = null; setPhase('') }
    }
  }, [apply])

  const selectSaved = (id) => {
    if (operation.current) return null
    const saved = savedConsultas.find((item) => item.consultaId === id)
    if (!saved || normalizeCnj(saved.numeroProcesso) !== resolvedProcess.numeroProcesso) return null
    const confirmed = apply(saved, { numeroProcesso: resolvedProcess.numeroProcesso })
    setNumeroProcesso(confirmed.numeroProcesso)
    setInstancia(confirmed.instancia)
    setRestoreId(confirmed.consultaId)
    setInstanceRequired(false)
    if (juditReconcilesUncertain(uncertain, confirmed, { consultas: savedConsultas, maisConsultas })) setUncertain(null)
    setError('')
    return confirmed
  }

  const startByProcess = ({ numeroProcesso: number, publicacao = publication, instancia: instance } = {}) => {
    const target = resolveJuditProcess(publicacao, number || processReference)
    if (operation.current || uncertain || target.error) { if (target.error) setError(target.error); return null }
    if (instance !== undefined && ![1, 2].includes(instance)) return null
    setSelected([])
    setVisibleText(null)
    setInstanceRequired(false)
    return perform('Buscando documentos deste processo…', (signal) => JuditProcessos.openByProcess({ numeroProcesso: target.numeroProcesso,
      publicacao, ...(instance ? { instancia: instance } : {}) }, signal), { numeroProcesso: target.numeroProcesso, ...(instance ? { instancia: instance } : {}) }, true)
  }

  const start = (numeroProcesso, instancia) => {
    if (operation.current || consulta || uncertain || requestId || !validCnj(numeroProcesso) || ![1, 2].includes(instancia) ||
      !configuration?.habilitado || !configuration?.chaveConfigurada) return null
    const id = crypto.randomUUID()
    setRequestId(id)
    setRequestLookupId(id)
    setRequestHistory((previous) => [...previous, { requestId: id, criadaEm: new Date().toISOString() }])
    setSelected([])
    setVisibleText(null)
    return perform('Solicitando documentos à Judit…', (signal) => JuditProcessos.create({ numeroProcesso, instancia, requestId: id }, signal),
      { numeroProcesso, instancia, requestId: id }, true)
  }
  const restore = (id) => perform('Lendo consulta salva…', (signal) => JuditProcessos.get(id, signal), { consultaId: id })
  const restoreRequest = (id) => perform('Recuperando consulta pela solicitação local…', (signal) => JuditProcessos.getByRequest(id, signal), {})
  const refresh = () => {
    const current = currentConsulta.current
    if (!current?.podeAlterar || juditRefreshWait(current) > 0) return null
    return perform('Consultando andamento da solicitação…', (signal) => JuditProcessos.refresh(current.consultaId, current.versao, signal), current, true)
  }
  const download = (anexo) => {
    const current = currentConsulta.current
    if (!current?.podeAlterar || !anexo.podeBaixar) return null
    return perform('Obtendo e extraindo documento…', (signal) => JuditProcessos.download(current.consultaId, current.versao, anexo.anexoId, signal), current, true)
  }
  const reextract = (anexo) => {
    const current = currentConsulta.current
    if (!current?.podeAlterar || !anexo.podeReextrair) return null
    return perform('Obtendo novamente e extraindo o documento…', (signal) => JuditProcessos.reextract(current.consultaId, current.versao, anexo.anexoId, signal),
      { ...current, operacao: 'REOBTER', anexoId: anexo.anexoId }, true)
  }
  const text = async (anexo) => {
    const current = currentConsulta.current
    if (operation.current || !current || !juditSelectable(anexo)) return
    const controller = new AbortController()
    operation.current = controller
    setPhase('Lendo texto extraído…')
    setError('')
    try {
      const response = await JuditProcessos.text(current.consultaId, anexo.documentoId, controller.signal)
      if (controller.signal.aborted || currentConsulta.current?.consultaId !== current.consultaId || currentConsulta.current?.versao !== current.versao) return
      const data = response.data
      if (!response.success || data?.documentoId !== anexo.documentoId || data?.versao !== anexo.documentoVersao ||
        data?.textoSha256 !== anexo.textoSha256 || data?.estadoExtracao !== 'EXTRAIDO' || !Array.isArray(data.paginas)) {
        setError('O texto não corresponde à versão e à integridade selecionadas. Releia a consulta salva.')
        return
      }
      setVisibleText({ ...data, titulo: anexo.titulo })
    } catch { if (!controller.signal.aborted) setError('Não foi possível ler o texto extraído. Nenhuma consulta externa foi iniciada.') }
    finally { if (operation.current === controller) { operation.current = null; setPhase('') } }
  }
  const toggle = (anexo) => {
    if (operation.current || !juditSelectable(anexo)) return
    setSelected((previous) => previous.some((item) => item.documentoId === anexo.documentoId)
      ? previous.filter((item) => item.documentoId !== anexo.documentoId)
      : previous.length < Math.min(JUDIT_MAX_DOCUMENTS, configuration?.maxDocumentos || JUDIT_MAX_DOCUMENTS) ? [...previous, juditSelection(anexo)] : previous)
  }
  const changeSelection = (id, changes) => {
    if (operation.current) return
    setSelected((previous) => previous.map((item) => item.documentoId === id ? { ...item, ...changes,
      ...(changes.tipo && changes.tipo !== item.tipo ? { posicaoAtualConfirmada: false } : {}) } : item))
  }
  const detach = () => {
    if (operation.current || uncertain || requestId && !consulta) return
    setConsulta(null); currentConsulta.current = null; setSelected([]); setVisibleText(null); setError(''); setRequestId('')
    setNumeroProcesso(''); setInstancia(''); setRestoreId('')
  }
  const selectionError = juditSelectionError(consulta, selected, publication, resolvedProcess.numeroProcesso)
  const context = juditGenerationContext(consulta, selected)
  return { configuration, configurationError, consulta, savedConsultas, maisConsultas, instanceRequired, resolvedProcess, selected, phase, error, uncertain, requestId, requestHistory, visibleText,
    numeroProcesso, setNumeroProcesso, instancia, setInstancia, restoreId, setRestoreId,
    requestLookupId, setRequestLookupId,
    selectionError, context, start, startByProcess, readByProcess, selectSaved, restore, restoreRequest, refresh, download, reextract, text, toggle, changeSelection, detach,
    closeText: () => setVisibleText(null),
    blocked: Boolean(phase || uncertain || consulta && selectionError || requestId && !consulta),
    hasWork: Boolean(consulta || requestId || phase || selected.length || requestHistory.length || numeroProcesso.trim() || restoreId.trim() || requestLookupId.trim()) }
}
